// Header name for wallet session token (must match backend NexusWalletContextMiddleware.WalletSessionHeader)
export const WALLET_SESSION_HEADER = 'X-Wallet-Session';

// Session storage keys
const WALLET_SESSION_TOKEN_KEY = 'walletSessionToken';
const WALLET_SESSION_EXPIRES_KEY = 'walletSessionExpires';
const WALLET_ADDRESS_KEY = 'walletAddress';

/**
 * Wallet session information
 */
export interface WalletSessionInfo {
  sessionToken: string;
  walletAddress: string;
  expiresAt: Date;
}

/**
 * Set the wallet session after unlocking
 */
export const setWalletSession = (session: WalletSessionInfo): void => {
  sessionStorage.setItem(WALLET_SESSION_TOKEN_KEY, session.sessionToken);
  sessionStorage.setItem(WALLET_SESSION_EXPIRES_KEY, session.expiresAt.toISOString());
  sessionStorage.setItem(WALLET_ADDRESS_KEY, session.walletAddress);
};

/**
 * Get the current wallet session
 */
export const getWalletSession = (): WalletSessionInfo | null => {
  const token = sessionStorage.getItem(WALLET_SESSION_TOKEN_KEY);
  const expires = sessionStorage.getItem(WALLET_SESSION_EXPIRES_KEY);
  const address = sessionStorage.getItem(WALLET_ADDRESS_KEY);
  
  if (!token || !expires || !address) return null;
  
  const expiresAt = new Date(expires);
  
  // Check if session has expired
  if (expiresAt <= new Date()) {
    clearWalletSession();
    return null;
  }
  
  return {
    sessionToken: token,
    walletAddress: address,
    expiresAt
  };
};

/**
 * Clear the wallet session (e.g., on logout or lock)
 */
export const clearWalletSession = (): void => {
  sessionStorage.removeItem(WALLET_SESSION_TOKEN_KEY);
  sessionStorage.removeItem(WALLET_SESSION_EXPIRES_KEY);
  sessionStorage.removeItem(WALLET_ADDRESS_KEY);
};

/**
 * Check if the wallet is currently unlocked
 */
export const isWalletUnlocked = (): boolean => {
  return getWalletSession() !== null;
};

/**
 * Get the current wallet address if unlocked
 */
export const getWalletAddress = (): string | null => {
  const session = getWalletSession();
  return session?.walletAddress ?? null;
};

// Note: unlockWallet and lockWallet require authorizedFetch from keycloakProvider
// They are exported from keycloakProvider.tsx to avoid circular dependencies
